import { Controller } from 'stimulus';

class CustomScrollbar extends Controller {
  connect() {
    // We've the this.element as wrapper, but the child is actually the
    // element that handles scrolling. The wrapper is only necessary
    // to hold the scrollbar element.
    this.scrollContainer = this.element.firstElementChild;

    // Used during movement of the bar
    this.isDragging = false;
    this.startPosition = 0;
    this.delta = 0;

    // Update the scrollbar size, based on for example the scrollable width
    this.updateScrollbarSizes();

    // This could be added as data-action, but that would not make the
    // element self-contained, since we've to add it to one of the children.
    this.scrollContainer.addEventListener('scroll', this.onScroll.bind(this));
  }

  disconnect() {
    this.scrollContainer.removeEventListener('scroll', this.onScroll.bind(this));
  }

  onWindowResize() {
    this.updateScrollbarSizes();
  }

  onScroll() {
    // If dragging, the scroll left and the bar thumbnail are already handled.
    if (this.isDragging) {
      return;
    }

    const { scrollLeft, scrollWidth } = this.scrollContainer;
    const { offsetWidth } = this.barTarget;

    const fromLeft = (scrollLeft / scrollWidth) * offsetWidth;

    this.handleTarget.style.left = `${fromLeft}px`;

    this.checkArrowsVisibility();
  }

  onMouseDown(event) {
    this.isDragging = true;

    if (event.touches) {
      event.preventDefault();
    }

    // Store the start position of the bar handle in pixels
    // based on the movement we +/- the value and move the handle
    this.startPosition = this.handleTarget.offsetLeft;
  }

  onWindowMouseMove(event) {
    if (!this.isDragging) {
      return;
    }

    const movementX = this.movementXForEvent(event);

    // The movementX is a number of pixels since the last call
    this.delta += movementX;

    const handleWidth = this.handleTarget.offsetWidth;
    const barWidth = this.barTarget.offsetWidth;

    // Calculate the position the handle should be moved to
    let position = this.startPosition + this.delta;

    // If the position is impossible on the left
    if (position < 0) {
      position = 0;

    // If the position is impossible on the right
    } else if (position > (barWidth - handleWidth)) {
      // Reset to right edge
      position = barWidth - handleWidth;
    }

    // Move the handle
    this.handleTarget.style.left = `${position}px`;

    // Calculate the percentage that we should be scrolled.
    const percent = position / (barWidth - handleWidth);

    const { scrollWidth, offsetWidth } = this.scrollContainer;

    // Scroll the scroll container to the place where the handle indicates
    this.scrollContainer.scrollLeft = (scrollWidth - offsetWidth) * percent;
  }

  onWindowMouseUp() {
    this.isDragging = false;
    this.delta = 0;
    this.startPosition = 0;
    this.previousTouch = null;
    this.checkArrowsVisibility();
  }

  checkArrowsVisibility() {
    const { scrollLeft, scrollWidth } = this.scrollContainer;

    const isEndReached = scrollLeft >= scrollWidth - this.scrollContainer.offsetWidth;

    if (this.hasArrowsTarget) {
      this.arrowsTarget.style.opacity = isEndReached ? 0 : 1;
    }
  }

  updateScrollbarSizes() {
    const { scrollWidth, offsetWidth } = this.scrollContainer;

    const hasScroll = scrollWidth > offsetWidth;

    this.barTarget.style.display = hasScroll ? 'block' : 'none';

    if (this.hasArrowsTarget) {
      this.arrowsTarget.style.display = hasScroll ? 'block' : 'none';
    }

    const computedStyle = window.getComputedStyle(this.scrollContainer);

    if (computedStyle.overflowX === 'visible') {
      return;
    }

    if (!hasScroll) {
      return;
    }

    const percentageVisible = offsetWidth / scrollWidth;

    this.handleTarget.style.width = `${percentageVisible * 100}%`;
  }

  /**
   * Touch events do not have a movementX property, so we have to calculate it
   * by comparing to the previous touch.
   */
  movementXForEvent(event) {
    // If mouse, it should've a movementX.
    if (event.movementX) {
      return event.movementX;
    }

    // Make sure we've touch events
    if (event.touches && event.touches.length) {
      const touch = event.touches[0];
      let movementX = 0;

      if (this.previousTouch) {
        movementX = touch.pageX - this.previousTouch.pageX;
      }

      this.previousTouch = touch;

      return movementX;
    }

    return 0;
  }

  onArrowsClick() {
    this.scrollContainer.scrollTo({
      left: this.scrollContainer.scrollLeft + this.scrollContainer.offsetWidth / 1.5,
      behavior: 'smooth',
    });
  }
}

CustomScrollbar.targets = [
  'bar',
  'handle',
  'arrows',
];

export default CustomScrollbar;
